/**
 * 专辑等多文件上传会连续请求 /api/upload；对 429 做退避重试，401 走统一鉴权续期。
 * 供 apiConfig.uploadFile、uploadUtils 统一复用。
 */

import { authenticatedFetch } from './authenticatedFetch';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function parseRetryAfterMs(header: string | null): number | null {
  if (!header) return null;
  const sec = parseInt(header, 10);
  if (!Number.isNaN(sec) && sec >= 0) {
    return Math.min(sec * 1000, 120_000);
  }
  const when = Date.parse(header);
  if (!Number.isNaN(when)) {
    const ms = when - Date.now();
    return ms > 0 ? Math.min(ms, 120_000) : null;
  }
  return null;
}

function backoffMs(attemptIndex: number): number {
  const exp = Math.min(30_000, 800 * 2 ** (attemptIndex - 1));
  return exp + Math.floor(Math.random() * 400);
}

export const UPLOAD_RATE_LIMIT_MAX_ATTEMPTS = 6;

export async function fetchWithUploadRateLimitRetry(
  url: string,
  init: RequestInit,
  maxAttempts: number = UPLOAD_RATE_LIMIT_MAX_ATTEMPTS
): Promise<Response> {
  const n = Math.max(1, maxAttempts);
  let last: Response | undefined;
  for (let attempt = 1; attempt <= n; attempt++) {
    last = await authenticatedFetch(url, init);
    if (last.ok || last.status !== 429) {
      return last;
    }
    if (attempt === n) {
      return last;
    }
    const headerMs = parseRetryAfterMs(last.headers.get('Retry-After'));
    await sleep(headerMs ?? backoffMs(attempt));
  }
  return last!;
}
